import axios from 'axios';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';

import styles from './CreateFavListForm.module.css';
import FormField from '../../../../UI/FormUI/FormField/FormField';
import Button from '../../../../UI/Buttons/Button/Button';
import { PlusIcon } from '../../../../../assets/images/icons';
import { getFavoriteTalents } from '../../../../../services/TalentsService';

function CreateFavListForm({ onSubmit, favTalents, setFavTalents, page, per_page, filters, onclose, talentId, ...props }) {
    const { t } = useTranslation();
    const user = useSelector(state => state.user.user);
    const [isLoading, setIsLoading] = useState(true);
    // Add isSubmitting state for the Add button
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Flag to track if user has manually changed selections
    const [userHasInteracted, setUserHasInteracted] = useState(false);
    // Ref to track if initial setup is complete
    const initialSetupComplete = useRef(false);

    const [jobOptions, setJobOptions] = useState([
        { id: 1, label: 'test', value: 'job1' },
        { id: 2, label: 'test 2', value: 'job2' },
        { id: 3, label: 'test 3', value: 'job3' },
    ]);
    
    // State to track lists that the talent is part of
    const [talentLists, setTalentLists] = useState([]);
    
    // Load the talent lists first
    useEffect(() => {
        if (userHasInteracted) {
            // Skip updating lists if user has manually changed selections
            return;
        }
        
        // Find the specific talent we're working with
        const currentTalent = favTalents.find(talent => talent.id === talentId);
         
        if (currentTalent) {
            // Collect all list IDs this talent belongs to
            const listIds = [];
            
            // Check for direct list_id
            if (currentTalent.list_id) {
                listIds.push(currentTalent.list_id);
            }
            
            // Check for lists array
            if (currentTalent.lists && Array.isArray(currentTalent.lists)) {
                currentTalent.lists.forEach(list => {
                    if (list.list_id) {
                        listIds.push(list.list_id);
                    }
                });
            }
            
            // Check for list_ids array
            if (currentTalent.list_ids && Array.isArray(currentTalent.list_ids)) {
                currentTalent.list_ids.forEach(id => {
                    listIds.push(id);
                });
            }
            
            // Also check other talents with matching id
            favTalents.forEach(talent => {
                if (talent.id === talentId && talent !== currentTalent) {
                    if (talent.list_id) {
                        listIds.push(talent.list_id);
                    }
                    
                    if (talent.lists && Array.isArray(talent.lists)) {
                        talent.lists.forEach(list => {
                            if (list.list_id) {
                                listIds.push(list.list_id);
                            }
                        });
                    }
                }
            });
            
            // Remove duplicates
            const uniqueListIds = [...new Set(listIds)];
             setTalentLists(uniqueListIds);
        }
    }, [favTalents, talentId, userHasInteracted]);

    useEffect(() => {
        const fetchFavoriteLists = async () => {
            try {
                setIsLoading(true);
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}talents/favorites/lists?userId=${user.id}`
                );
                const fetchedLists = response.data.favoriteLists.map((item) => ({
                    id: item.id,
                    label: item.list_name,
                    value: `list_${item.id}`, // Use consistent value format with list_ prefix
                }));

                setJobOptions(fetchedLists);
                setIsLoading(false);
                
                // Only update selected jobs if user hasn't interacted with the form yet
                if (!userHasInteracted) {
                    updateSelectedJobs(fetchedLists);
                }
            } catch (error) {
                 setIsLoading(false);
            }
        };

        fetchFavoriteLists();
    }, [user.id]);
    
    // Function to update selected jobs based on talent lists
    const updateSelectedJobs = (options) => {
        if (userHasInteracted) {
            // Skip updating if user has manually changed selections
            return;
        }
        
        if (talentLists.length > 0 && options.length > 0) {
            const preselectedJobs = options
                .filter(job => talentLists.includes(job.id))
                .map(job => job.value);
            
             formik.setFieldValue('selectedJobs', preselectedJobs);
            // Mark initial setup as complete after setting the initial values
            initialSetupComplete.current = true;
        }
    };
    
    // Update selected jobs when job options or talent lists change
    useEffect(() => {
        // Only update if user hasn't interacted and we have the necessary data
        if (!userHasInteracted && jobOptions.length > 0 && talentLists.length > 0) {
            updateSelectedJobs(jobOptions);
        }
    }, [talentLists, jobOptions, userHasInteracted]);
    
    const handleFav = async (values) => {
        try {
            // Set submitting state to true to show the loader
            setIsSubmitting(true);
            
            // Store the current selections before submission
            const currentSelections = [...values.selectedJobs];
             
            const selectedListIds = currentSelections.map((jobValue) => {
                // Extract the ID from the value (remove list_ prefix)
                const matchedJob = jobOptions.find((job) => job.value === jobValue);
                return matchedJob?.id;
            }).filter(Boolean);
            
             
            // Submit the changes to the server
            await axios.post(`${process.env.REACT_APP_API_URL}talents/favorites/add`, {
                userId: user.id,
                talentId: talentId,
                listIds: selectedListIds,
            });
            
            // Only update the parent state after successful submission
            // This will refresh the data in the parent component
            const responseTalent = await getFavoriteTalents(user.id, page, per_page, filters);
            setFavTalents(responseTalent.talents);
            
            // Dispatch a custom event to notify ProfileSideBar component to refresh lists
            const listsUpdatedEvent = new CustomEvent('jobseekerListsUpdated', {
                detail: {
                    userId: user.id,
                    updatedListIds: selectedListIds
                }
            });
            window.dispatchEvent(listsUpdatedEvent);
            
            // Show success message
            toast.success(currentSelections.length > 0 ?
                t('job.user_added_to_favorites') :
                t('job.user_removed_from_favorites'));
                
            // Close the modal
            onclose();
        } catch (error) {
             toast.error(t('general.error.something_went_wrong'));
            // Set submitting state to false if there's an error
            setIsSubmitting(false);
        }
    }
    
    const formik = useFormik({
        initialValues: {
            listName: '',
            selectedJobs: [],
        },
        onSubmit: (values) => {
            // Call handleFav directly with the current values
            handleFav(values);
        },
    });

    const handleCheckboxChange = (jobValue) => {
        // Set userHasInteracted to true when user interacts with checkboxes
        if (initialSetupComplete.current && !userHasInteracted) {
            setUserHasInteracted(true);
        }
        
        const { selectedJobs } = formik.values;
        if (selectedJobs.includes(jobValue)) {
            formik.setFieldValue(
                'selectedJobs',
                selectedJobs.filter(item => item !== jobValue)
            );
        } else {
            formik.setFieldValue('selectedJobs', [...selectedJobs, jobValue]);
        }
        
     };

    const addJobToList = async () => {
        if (formik.values.listName.trim() !== '') {
            try {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}talents/favorites/list`, {
                    userId: user.id,
                    listName: formik.values.listName,
                });
                
                // Get the newly created list's ID from the response
                const newListId = response.data.listId;
                
                const newJobOption = {
                    id: newListId,
                    label: formik.values.listName,
                    value: `list_${newListId}`,
                };
                
                // Update job options without triggering automatic selection updates
                setJobOptions(prev => [...prev, newJobOption]);
                formik.setFieldValue('listName', '');
                
                // Dispatch a custom event to notify ProfileSideBar component about the new list
                const newListCreatedEvent = new CustomEvent('jobseekerListsUpdated', {
                    detail: {
                        userId: user.id,
                        newList: {
                            id: newListId,
                            list_name: formik.values.listName
                        }
                    }
                });
                window.dispatchEvent(newListCreatedEvent);
            } catch (error) {
                console.error("Error creating new list:", error);
                toast.error(t('general.error.something_went_wrong'));
            }
        }
    };

    return (
        <form 
            onSubmit={(e) => {
                // Prevent default form submission behavior
                e.preventDefault();
                // Get current form values
                const currentValues = formik.values;
                // Call our handler directly with the current values
                handleFav(currentValues);
            }} 
            {...props}
        >
            <div>
                <div className={styles.contentBody}>
                    <div style={{ width: "100%" }}>
                        <FormField
                            name="listName"
                            type="text"
                            placeholder={t('forms.CreateFavList.listName')}
                            hasBorder
                            onChange={formik.handleChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addJobToList();
                                }
                            }}
                            style={{ width: "100%" }}
                            value={formik.values.listName}
                            error={formik.touched.listName && formik.errors.listName}
                        />
                    </div>
                    <img
                        src={PlusIcon}
                        onClick={addJobToList}
                        style={{ marginBottom: 20, cursor: "pointer" }}
                    />
                </div>
                {isLoading ? (
                    <div className={styles.loaderContainer}>
                        <div className={styles.loader}></div>
                        <p>{t('forms.login.processing')}</p>
                    </div>
                ) : (
                    <div className={`${styles.checkboxWrapper} ${styles.listContainer}`}>
                        {jobOptions.map((job) => (
                            <div key={job.id} className={styles.checkboxItem} onClick={() => handleCheckboxChange(job.value)}>
                                <input
                                    type="checkbox"
                                    id={`job-${job.id}`}
                                    value={job.value}
                                    checked={formik.values.selectedJobs.includes(job.value)}
                                    onChange={() => handleCheckboxChange(job.value)}
                                    className={styles.checkbox}
                                />
                                <label htmlFor={`job-${job.id}`} className={styles.checkboxLabel}>
                                    {job.label}
                                </label>
                            </div>
                        ))}
                    </div>
                )}
                {formik.touched.selectedJobs && formik.errors.selectedJobs && (
                    <div className={styles.error}>{formik.errors.selectedJobs}</div>
                )}
            </div>

            <div>
                <Button 
                    type="submit" 
                    className={styles.button}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <div className={styles.buttonLoader}>
                            <div className={styles.spinner}></div>
                            <span>{t('forms.login.processing')}</span>
                        </div>
                    ) : (
                        t('forms.workExperience.add')
                    )}
                </Button>
            </div>
        </form>
    );
}

export default CreateFavListForm;
